import { assert } from '../app/logger.js';
import { services } from '../app/services.js';
import { session } from '../app/session.js';
import { AsyncDataPipeableArray } from '../base/pipeable/async_data_pipeable_array.js';
import { Trip } from './trip.js';
import { TripsPipeables } from './trips_pipeables.js';

const kCopyName = ' Copy';
const kTrimWords = [
  /Provincial/,
  /Park(ing)?/,
  'Campground',
  'Backpacking',
  /Trails?/,
  /Wildlands/,
  /Highlands/,
  /Smoothwater/,
  'ZEC',
  'Water',
  'Road',
  'Access Point',
  'Gate',
  'Office',
];

const adminRights = ['read', 'write', 'share', 'all'];

/**
 * Trips.
 */
class Trips extends AsyncDataPipeableArray {
  static localeDateRangeStr(startDate, duration) {
    // No Date.parse() because it takes 'YYYY-MM-DD' as UTC date while all other
    // methods operates with local time.
    let lexems = startDate.split('-');
    assert(
      lexems && lexems.length == 3,
      `[data] wrong date format given: ${startDate}`
    );

    let sdate = new Date(lexems[0], lexems[1] - 1, lexems[2]);
    let days = parseInt(duration) - 1;
    if (days == 0) {
      let m = sdate.toLocaleDateString({}, { month: 'long' });
      return `${m} ${sdate.getDate()}`;
    }

    let edate = new Date(sdate);
    edate.setDate(sdate.getDate() + days);
    if (sdate.getMonth() == edate.getMonth()) {
      let m = sdate.toLocaleDateString({}, { month: 'long' });
      return `${m} ${sdate.getDate()}-${edate.getDate()}`;
    }

    let sm = sdate.toLocaleDateString({}, { month: 'long' });
    let em = sdate.toLocaleDateString({}, { month: 'long' });
    return `${sm} ${sdate.getDate()} - ${em} ${edate.getDate()}`;
  }

  static weekendDateStr() {
    let nowDate = new Date();
    let weekDay = nowDate.getDay();
    var isWeekend = weekDay == 6 || weekDay == 0;
    let weekendDay = nowDate.getDate() + 6 + (isWeekend ? 6 : 0) - weekDay;
    return new Date(nowDate.getFullYear(), nowDate.getMonth(), weekendDay)
      .toISOString()
      .substring(0, 10);
  }

  static sortTrips(trips) {
    trips.sort(this.Sort);
    return trips;
  }

  // Returns planned trips.
  get plannedTrips() {
    return this.data.filter(trip => trip.isPlanned);
  }

  // Returns years list of all past trips.
  get pastTripYears() {
    return Array.from(
      this.filter(trip => !trip.isPlanned)
        .reduce((set, trip) => set.add(trip.year), new Set())
        .keys()
    );
  }

  // Returns past trips of the given year.
  getPastTrips(year) {
    return this.filter(trip => !trip.isPlanned && trip.year == year);
  }

  // Returns true if there's one trip at least.
  hasTrips() {
    return this.data.length > 0;
  }

  // Returns a trip by trip id.
  findTrip({ id }) {
    return this.data.find(trip => trip.id == id);
  }

  // Returns a first trip.
  get firstTrip() {
    return this.plannedTrips[0] || this.data[this.data.length - 1] || null;
  }

  //
  // Modification

  // Creates a new trip for the given trip options.
  async createTrip({
    activities,
    loc,
    startDate,
    duration,
    groupSize,
    cocampers,
  }) {
    assert(loc, `[data] createTrip: no loc`);

    // Auto generate a trip name based on its location and date.
    let title = loc.name;
    for (let word of kTrimWords) {
      title = title.replace(word, '');
    }
    title = title.replace(/\s+/g, ' ').trim();
    title += ` — ${Trips.localeDateRangeStr(startDate, duration)}`;

    // Add new trip.
    let { id } = await services.trip.createTrip({
      title,
      activities,
      startDate: startDate,
      duration,
      groupSize: groupSize,
      locId: loc.id,
      cocampers,
    });
    if (!id) {
      return null;
    }

    // Update trip data cache.
    return this.#insertTrip({
      id,
      title,
      activities,
      startDate,
      duration,
      groupSize,
      loc,
      cover: loc.cover,
      rights: adminRights,
      cocampers,
    });
  }

  // Clones the given trip.
  async cloneTrip(origTrip) {
    let startDate = Trips.weekendDateStr();
    let duration = origTrip.duration;
    let dates = Trips.localeDateRangeStr(startDate, duration);

    let dashIndex = origTrip.title.indexOf(' — ');
    let title =
      (dashIndex == -1 && origTrip.title) ||
      origTrip.title.substring(0, dashIndex);
    title = `${title}${kCopyName} — ${dates}`;

    let { id } = await services.trip.cloneTrip({
      id: origTrip.id,
      title,
      activities: origTrip.activities,
      startDate: startDate,
      duration,
      groupSize: origTrip.groupSize,
      locId: origTrip.loc.id,
      cocampers: origTrip.cocampers,
    });
    if (!id) {
      return null;
    }

    return this.#insertTrip({
      id,
      title,
      activities: origTrip.activities,
      startDate,
      duration,
      groupSize: origTrip.groupSize,
      loc: origTrip.loc,
      cover: origTrip.loc.cover,
      rights: adminRights,
      cocampers: [],
    });
  }

  // Removes the trip.
  removeTrip(trip) {
    this.remove(trip);
    return services.trip.removeTrip(trip.id);
  }

  // Rejects the shared trip.
  rejectSharedTrip(trip) {
    this.remove(trip);
    return services.trip.rejectShare(trip.inviteHash);
  }

  // Returns information about trip invitation.
  getShareInfo(inviteHash) {
    return services.trip.getShareInfo(inviteHash);
  }

  // Accepts the invite.
  acceptShare(inviteHash) {
    return services.trip.acceptShare(inviteHash);
  }

  reloadCocampers({ id }) {
    let trip = this.findTrip({ id });
    assert(trip, `[data] no trip for ${id} id`);
    return trip && trip.reloadCocampers();
  }

  get dataClassName() {
    return 'trips';
  }

  getLoadPromise() {
    return services.trip.getTrips().then(trips => this.#unpackData(trips));
  }

  #insertTrip(options) {
    return this.insert(new this.constructor.Trip(options));
  }

  #unpackData(trips) {
    let list = [];
    for (let trip of trips) {
      list.push(
        new this.constructor.Trip({
          id: trip.id,
          title: trip.title,
          status: trip.status,
          activities: trip.activities,
          startDate: trip.startDate,
          duration: trip.duration,
          groupSize: trip.groupSize,
          cover: trip.cover,
          loc: {
            id: trip.locId,
            name: trip.loc_name,
            type: trip.loc_type,
            googleId: trip.google_id,
            lat: trip.lat,
            lng: trip.lng,
          },
          rights: trip.rights,
          inviteHash: trip.inviteHash,
          cocampers: trip.cocampers,
        })
      );
    }

    return list;
  }

  static Trip = Trip;
  static Pipeables = TripsPipeables;
  static Sort = TripsPipeables.TripSort;

  static dataDeps = {
    array: ['startDate', 'duration'],
  };

  toString() {
    return `[Array:Trips]`;
  }
}

const trips = new Trips();
session.hookOnChange(user => {
  trips.clear();
  if (user) {
    trips.load();
  }
});

export { Trips, trips };
